import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useDispatch, useSelector } from 'react-redux';
import { BookingFilterAny } from 'stores/rematch/models/types/booking-filter';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { TimezoneSelectorMode } from 'utilities/enum-utils';
import { usePortalUser } from 'utilities/react-hooks/use-portal-user';
import {
  BookingDrawerContentType,
  bookingsViewContextInitialState,
  BookingsViewContextType,
  OnSelectRosterItemOptions,
  RosterPeriodView,
} from './types';
import { useDisplayTimezone } from './use-display-timezone';
import { useDisclosure } from '@good/ui/hooks';
import CreateNewBookingModal from 'views/bookings/listings/components/CreateNewBookingModal';
import { useHistory } from 'react-router-dom';
import { Box } from '@good/ui/core';
import { IRosterBooking, IRosterShift } from '../bookings-roster-view/types';
import { BookingsViewType } from '../booking-view-toolbar/types';
import { ROSTER_TAB } from '../bookings-roster-view/components/roster-common-enums';
import { Updater } from '@tanstack/react-table';
import { BookingDrawer } from './booking-drawer';
import { useBookingsQuery } from './bookings-query-context';
import { useTakeInitialSnapshot } from './use-take-initial-snapshot';

export const BookingsViewContext = createContext<BookingsViewContextType>(bookingsViewContextInitialState);

interface BookingsViewProviderProps {
  children: React.ReactNode;
  view: BookingsViewType;
}

export const BookingsViewProvider = ({ children, view }: BookingsViewProviderProps) => {
  const history = useHistory();
  const [rosterPeriod, setRosterPeriod] = useState<RosterPeriodView>('weekly');
  const { bookingsStore } = useDispatch<IRootDispatch>();
  const portalUser = usePortalUser();
  const bookingDisplayTzMode = useSelector((state: IRootState) => state.bookingsStore.bookingDisplayTzMode);
  const [displayTimezone, setDisplayTimezone] = useDisplayTimezone();
  const [createBookingOpened, { open: openCreateBooking, close: closeCreateBooking }] = useDisclosure(false);
  const [selectedCustomer, setSelectedCustomer] = useState<unknown | undefined>(undefined);
  const [selectedCreateDate, setSelectedCreateDate] = useState<Date | undefined>(undefined);
  const sideMenuCollapsed = useSelector((state: IRootState) => state.navigationStore.sideMenuCollapsed);
  const [drawerOpened, setDrawerOpened] = useState<boolean>(false);
  const [canCloseDrawer, setCanCloseDrawer] = useState<boolean>(true);
  const [selectedRosterItem, setSelectedRosterItem] = useState<IRosterBooking | IRosterShift | null>(null);
  const [selectedRosterItemType, setSelectedRosterItemType] = useState<BookingDrawerContentType | null>(null);
  const [selectedRosterTab, setSelectedRosterTab] = useState<ROSTER_TAB | null>(null);
  const refetchRoster = useRef<(() => Promise<void>) | null>(null);
  const fetchBookings = useBookingsQuery((state) => state.fetchBookings);
  const onChangeShowActionSheet = useBookingsQuery((state) => state.onChangeShowActionSheet);
  const onChangeIndeterminateCheck = useBookingsQuery((state) => state.onChangeIndeterminateCheck);
  const onChangeCheckAllIndicator = useBookingsQuery((state) => state.onChangeCheckAllIndicator);
  useTakeInitialSnapshot();

  const registerRefetchRoster = useCallback((refetch: () => Promise<void>) => {
    refetchRoster.current = refetch;
  }, []);

  const onDeselectRosterItem = useCallback(() => {
    setSelectedRosterItem(null);
    setSelectedRosterItemType(null);
    setSelectedRosterTab(null);
  }, []);

  const toggleDrawer = useCallback(
    (nextOpenState: boolean) => {
      if (nextOpenState) {
        setDrawerOpened(nextOpenState);
      } else if (!nextOpenState && canCloseDrawer) {
        onDeselectRosterItem();
        setDrawerOpened(nextOpenState);
      }
    },
    [canCloseDrawer, onDeselectRosterItem],
  );

  const openDrawer = useCallback(() => toggleDrawer(true), [toggleDrawer]);
  const closeDrawer = useCallback(() => toggleDrawer(false), [toggleDrawer]);

  const onSelectRosterItem = useCallback(
    (next: Updater<OnSelectRosterItemOptions> | OnSelectRosterItemOptions) => {
      if (typeof next === 'function') {
        let prevItem: IRosterBooking | IRosterShift | null = null;
        let prevType: BookingDrawerContentType | null = null;
        let prevTab: ROSTER_TAB | null = null;
        setSelectedRosterItem((prev) => {
          prevItem = prev;
          setSelectedRosterItemType((prevTypeState) => {
            prevType = prevTypeState;
            setSelectedRosterTab((prevTabState) => {
              prevTab = prevTabState;
              const { tab } = next({ item: prevItem, type: prevType, tab: prevTab });
              return tab ?? prevTab;
            });
            const { type } = next({ item: prevItem, type: prevType, tab: prevTab });
            return type ?? prevType;
          });
          const { item } = next({ item: prevItem, type: prevType, tab: prevTab });
          return item ?? prevItem;
        });
        openDrawer();
        return;
      }

      const { item, type, tab } = next;
      setSelectedRosterItem(item);
      setSelectedRosterItemType(type);
      setSelectedRosterTab(tab);
      openDrawer();
    },
    [openDrawer],
  );

  const onOpenCreateBooking = useCallback(
    (opts?: {
      customer?: unknown;
      startDate?: Date;
    }) => {
      openCreateBooking();
      setSelectedCustomer(opts?.customer ?? undefined);
      setSelectedCreateDate(opts?.startDate ?? undefined);
    },
    [openCreateBooking],
  );

  const onCloseCreateBooking = useCallback(() => {
    closeCreateBooking();
    setSelectedCustomer(undefined);
    setSelectedCreateDate(undefined);
  }, [closeCreateBooking]);

  const onChangeTimezone = useCallback(
    async (type: 'mode' | 'customTimezone', value: TimezoneSelectorMode) => {
      if (type === 'mode') {
        await bookingsStore.setBookingDisplayTzMode(value);
        setDisplayTimezone((prev) =>
          value === TimezoneSelectorMode.MyTimezone
            ? portalUser?.timezone
            : value === TimezoneSelectorMode.BookingTimezone
              ? null
              : bookingDisplayTzMode
                ? bookingDisplayTzMode
                : prev,
        );
      } else if (type === 'customTimezone') {
        await bookingsStore.setBookingDisplayTzCustom(value);
        setDisplayTimezone(value);
      }
    },
    [
      bookingDisplayTzMode,
      portalUser?.timezone,
      bookingsStore.setBookingDisplayTzMode,
      bookingsStore.setBookingDisplayTzCustom,
      setDisplayTimezone,
    ],
  );

  const onChangeFilter = useCallback(
    (filters: BookingFilterAny[]) => {
      bookingsStore.setHasBookingListingFilterChanged(true);
      bookingsStore.setBookingsFilterV2(filters);
      fetchBookings({ filters, reset: true, caller: 'BookingsViewProvider.onChangeFilter' });
    },
    [bookingsStore.setHasBookingListingFilterChanged, bookingsStore.setBookingsFilterV2, fetchBookings],
  );

  const retry = useCallback(async () => {
    onChangeCheckAllIndicator(false);
    onChangeIndeterminateCheck(false);
    onChangeShowActionSheet(false);
    await fetchBookings({
      allViews: true,
      reset: true,
      initialFetch: true,
      caller: 'retry',
    });
  }, [fetchBookings, onChangeCheckAllIndicator, onChangeIndeterminateCheck, onChangeShowActionSheet]);

  const refreshListings = useCallback(async () => {
    onChangeCheckAllIndicator(false);
    onChangeIndeterminateCheck(false);
    onChangeShowActionSheet(false);

    await fetchBookings({
      allViews: true,
      reset: true,
      caller: 'BookingsViewProvider.refreshListings',
    });

    if (view === 'calendar') {
      refetchRoster.current?.();
    }
  }, [fetchBookings, onChangeCheckAllIndicator, onChangeIndeterminateCheck, onChangeShowActionSheet, view]);

  const onChangeRosterPeriod = useCallback((period: RosterPeriodView) => {
    setRosterPeriod(period);
  }, []);

  const drawerWidth = '25rem';

  const pageWidth = useMemo(() => {
    if (!drawerOpened) {
      return '100%';
    }

    if (!sideMenuCollapsed) {
      return `calc(100vw - ${drawerWidth} - var(--app-shell-navbar-width))`;
    }

    return `calc(100vw - ${drawerWidth})`;
  }, [sideMenuCollapsed, drawerOpened]);

  const value: BookingsViewContextType = useMemo(
    () => ({
      displayTimezone,
      drawerOpened,
      onChangeFilter,
      onChangeRosterPeriod,
      onChangeTimezone,
      onDeselectRosterItem,
      onOpenCreateBooking,
      onSelectRosterItem,
      refreshListings,
      registerRefetchRoster,
      retry,
      rosterPeriod,
      selectedRosterItem,
      view,
    }),
    [
      displayTimezone,
      drawerOpened,
      onChangeFilter,
      onChangeRosterPeriod,
      onChangeTimezone,
      onDeselectRosterItem,
      onOpenCreateBooking,
      onSelectRosterItem,
      refreshListings,
      registerRefetchRoster,
      retry,
      rosterPeriod,
      selectedRosterItem,
      view,
    ],
  );

  const refreshAndClose = useCallback(() => {
    setDrawerOpened(false);
    refetchRoster.current?.();
  }, []);

  return (
    <BookingsViewContext.Provider value={value}>
      <Box
        w={pageWidth}
        style={{
          transition: 'width 0.3s ease-in-out',
        }}
      >
        {children}
      </Box>
      <CreateNewBookingModal
        isOpen={createBookingOpened}
        history={history}
        closeCreateBookingModal={onCloseCreateBooking}
        incomingCustomer={selectedCustomer}
        incomingStartDateTime={selectedCreateDate}
      />
      <BookingDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size={drawerWidth}
        refreshAndClose={refreshAndClose}
        selectedTab={selectedRosterTab}
        selectedType={selectedRosterItemType}
        onChangeCanCloseDrawer={(canClose) => {
          setCanCloseDrawer(canClose);
        }}
      />
    </BookingsViewContext.Provider>
  );
};

type Selector<T, Context> = (value: Context) => T;

export const useBookings = <T,>(selector: Selector<T, BookingsViewContextType>) =>
  useContextSelector(BookingsViewContext, selector);
